import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogOut, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";

const studentNav = [
  { to: "/dashboard", label: "Today" },
  { to: "/library", label: "Library" },
  { to: "/curriculum", label: "Curriculum" },
  { to: "/practice", label: "Practice" },
  { to: "/stuck", label: "Stuck on" },
  { to: "/progress", label: "Progress" },
  { to: "/agents", label: "Agents" },
  { to: "/profile", label: "Profile" },
];

export const Shell = ({ children, nav = studentNav }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);

  return (
    <div className="min-h-screen grain">
      <header className="sticky top-0 z-50 bg-[#faf8f5]/85 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-6">
          <Link to={user?.role === "guardian" ? "/guardian" : user?.role === "admin" ? "/admin" : "/dashboard"} className="flex items-center gap-2 shrink-0 hover:opacity-80 transition-opacity" data-testid="shell-logo">
            <Logo to={null} testId="shell-logo-mark" />
          </Link>
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  pathname.startsWith(item.to) ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200/60"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="flex items-center gap-3 ml-auto">
            <span className="hidden sm:block text-sm text-slate-500" data-testid="shell-user">
              {user?.name} <span className="rule-label ml-1">{user?.role}</span>
            </span>
            <button
              data-testid="shell-logout"
              onClick={() => {
                signOut();
                navigate("/login");
              }}
              className="p-2 rounded-full hover:bg-slate-200/70 transition-colors"
              title="Sign out"
            >
              <LogOut className="w-4 h-4 text-slate-600" />
            </button>
            <button data-testid="shell-menu-toggle" onClick={() => setOpen(!open)} className="md:hidden p-2 rounded-full hover:bg-slate-200/70 transition-colors" title="Menu">
              {open ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="md:hidden border-t border-slate-200/70 px-4 py-3 space-y-1" data-testid="shell-mobile-nav">
            {nav.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                className={`block px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                  pathname.startsWith(item.to) ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-200/60"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </header>
      <main className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">{children}</main>
    </div>
  );
};

export const guardianNav = [
  { to: "/guardian", label: "Overview" },
  { to: "/agents", label: "Agents" },
];
